import { urlUtil, util } from '../util';
import { AuthService } from '../services/auth/auth.service';
import { ConnectionService } from '../services/auth/connection.service';
import { IExtendedWindow, IObject, IPrice } from '../../../type_definitions/datatypes';
import {  Inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { EVENT_CLOSE_DIALOG, GaEventService, IGAEvent } from '../services/ga-event.service';
import { GaStateService } from '../services/ga-state.service';
import { GaDeferredService } from '../services/ga-deferred.service';
import { StateService } from '@uirouter/angular';

export const MESSAGE_GOTO_HOMEPAGE:string = 'goToHomePage';
export const MESSAGE_MENU_CHANGED:string = 'menuChanged';
export const MESSAGE_CHANGE_MENU:string = 'changeMenu';
export const MESSAGE_MENU_READY:string = 'menuReady';
export const MESSAGE_USERPROFILE_SAVED:string = 'userProfileSaved';
export const MESSAGE_EDITMODE_CHANGED:string = 'editModeChanged';
export const MESSAGE_TICKETGUIDE_SHOW:string = 'showTicketGuide';
export const MESSAGE_TICKETGUIDE_SAVED:string = 'postTicketGuide';
export const MESSAGE_PAYMENT_UPDATED:string = 'paymentUpdated';
export const MESSAGE_PAYMENT_SAVING:string = 'paymentSaving';
export const MESSAGE_PAYMENT_SAVED:string = 'paymentSaved';
export const MESSAGE_PAYMENT_SAVE_FAILED:string = 'paymentSaveFailed';
export const MESSAGE_SAVE_ORDER:string = 'saveOrder';
export const MESSAGE_GET_CART:string = 'getCart';
export const MESSAGE_ASSET_SAVING:string = 'assetSaving';
export const MESSAGE_ASSET_SAVED:string = 'assetSaved';
export const MESSAGE_ASSET_SAVE_FAILED:string = 'assetSaveFailed';
const MESSAGE_CLOSE_DIALOG:string = 'closeDialog';
export const MESSAGE_ASSET_VALIDATION_FAILED:string = 'assetValidationFailed';
export const MESSAGE_CHECK_SOW:string = 'checkSOW';
export const MESSAGE_SOW_RESULT:string = 'sowResult';
export const MESSAGE_CHANGE_BILLING_TYPE:string = 'changeBillingType';
export const MESSAGE_OPEN_BROWSELIST_EDIT_DIALOG:string = 'openBrowselistEditDialog';
export const MESSAGE_BROWSELIST_EDIT_DIALOG_CLOSED:string = 'browselistEditDialogClosed';
export const MESSAGE_OPEN_SEND_INVOICE_MAIL_DIALOG:string = 'openSendInvoiceMailDialog';

export const EVENT_GOTO_HOMEPAGE:string = 'mainUrl';
export const EVENT_EDITMODE_CHANGED:string = 'editModeChanged';
export const EVENT_TICKETDIALOG_SHOW:string = 'showTicketDialog';
export const EVENT_MENU_CHANGED:string = 'menuChanged';
export const EVENT_MENU_READY:string = 'menuReady';
export const EVENT_SAVE_ORDER:string = 'saveOrder';
export const EVENT_CHECK_SOW:string = 'checkSOW';
export const EVENT_CHANGE_BILLING_TYPE:string = 'changeBillingType';
export const EVENT_OPEN_BROWSELIST_EDIT_DIALOG:string = 'openBrowselistEditDialog';
export const EVENT_OPEN_SEND_INVOICE_MAIL_DIALOG:string = 'openSendInvoiceMailDialog';

export interface IGA1OutgoingCartItem {
  id:number;
  count:number;
  product:{
    productId:number;
    part:string;
    title:string;
    shortDescription:string;
    price:IPrice;
    unitLeasePrice?:IPrice|number;
    leasePeriod?:number;
    leasePriceTotal?:IPrice|number;
    partSuffix?:string; // for catalog items
    partSuffixDescription?:string; // for catalog items
    leadTime?:{
      days:number;
      warning:boolean;
      text?:string;
    }; // for catalog items

    category?:string; // for punchout items
    oppositePriceConversion?:boolean; // for punchout items
    vendorId?:number; // for punchout items
    catalogId?:number; // for punchout items
  };
}

export interface IGA1IncomingCartItem {
  id:number;
  count?:number;
  orderLineNumber?:number;
  itemId?:number | string;
}

export interface IGA1Message {
  message:string;
  messageId?:number;
  data:any;
}


/**
 * @ngdoc service
 * @name ga2.shared.services:ga1Service
 * @description
 *  Service for interaction with GA1 (if running embedded)
 */
@Injectable()
export class GA1Service {
  public static URLTYPE_ORDER_SUPPORT:string = 'ORDER_SUPPORT';
  public static URLTYPE_SURVEY_EDIT:string = 'SURVEY_EDIT';
  public static URLTYPE_CREATE_COMPANY:string = 'CREATE_COMPANY';
  public static URLTYPE_ACSL_TC:string = 'ACSL_TC';
  public static URLTYPE_BILLING_TYPE:string = 'BILLING_TYPE';
  public static URLTYPE_WEBSHOP_VIEW_MARGINS:string = 'WEBSHOP_VIEW_MARGINS';
  public static URLTYPE_WEBSHOP_NEW_MARGIN:string = 'WEBSHOP_NEW_MARGIN';
  public static URLTYPE_CREATE_LOCATION:string = 'CREATE_LOCATION';
  public static URLTYPE_UPLOAD_INVOICE_ADDRESSES:string = 'UPLOAD_INVOICE_ADDRESSES';
  public static URLTYPE_SUPPORT_TICKET:string = 'SUPPORT_TICKET';
  public static URLTYPE_UPLOAD_CATALOG:string = 'UPLOAD_CATALOG';

  private nextMessageId:number = (new Date()).getTime();
  private EXPIRY:number = 30000;
  private targetOrigin:string;

  //for every product that is being added, this will contain (per productId):
  // -promise: a promise that notifies the caller of addToCart
  // -expireTimer: a timer that is being used for noticing expired addToCart calls
  private addingProducts:IObject = {};

  protected _editMode:boolean;
  protected _subject:Subject<IGAEvent> = new Subject();
  public events:Observable<IGAEvent> = this._subject.asObservable();

  constructor(
    @Inject(Window) private window:IExtendedWindow,
    private deferredService:GaDeferredService,
    private stateService:StateService,
    private authService:AuthService,
    private gaStateService:GaStateService,
    private gaEventService:GaEventService
  ) {
    $(window).bind('message', this.onMessage);
  }

  /**
   * @ngdoc method
   * @name ga2.shared.services:ga1Service#embedded
   * @methodOf ga2.shared.services:ga1Service
   * @description
   *  Is the page embedded inside GA1 frames or not?
   * @returns {boolean} inside frame or not?
   */
  public embedded():boolean {
    return self !== parent;
  }

  public sendMessage(messageType:string, data?:any, messageId?:number):number {
    //use JSON, because IE8 doesn't allow sending Objects
    const messageObject:IGA1Message = {
      message: messageType,
      messageId: messageId,
      data: data
    };

    this.postMessage(messageObject);

    return messageId;
  }

  public postMessage(messageObject:IObject):void {
    this.window.cyWindowStub?.call(null, messageObject);
    console.debug('GA2 post message', messageObject.message);
    const messageJson = util.toJson(messageObject);

    const targetOrigin:string = this.getTargetOrigin();

    // post the message to all frames in all windows
    let w:IExtendedWindow = this.window;

    while (w) {
      for (let i = 0; w.top && w.top.frames && i < w.top.frames.length; i++) {
        const frame = w.top.frames[ i ];
        if (this.window !== frame) { //do not send to self
          frame.postMessage(messageJson, targetOrigin);
        }
      }

      w = (w === w.opener? undefined : w.opener);
    }
  }

  public getGA1Url(type:string, params?:any, options?:IObject):string {
    options = options || {};
    options.hideTop = util.notNullOrUndefinedOrEmpty(options?.hideTop)? options.hideTop : true;
    options.hideMenu = util.notNullOrUndefinedOrEmpty(options?.hideMenu)? options.hideMenu : true;

    // when options.redirect = TRUE -> /ga2/#/unacc?url=%2Funacc%2F... vs FALSE -> /unacc/...
    const getUrl = (ga1Url:string, params?:any, options?:IObject) => {
      let url:string = options?.redirect? this.getMainUnaccUrl(ga1Url) : ga1Url;
      if (params) {
        const queryParams:string = '?' + urlUtil.paramsToQueryString(params);
        url += options?.redirect? encodeURIComponent(queryParams) : queryParams;
        url += !params?.menu && this.stateService.params.menu? `&menu=${this.stateService.params.menu}` : '';
        url += options.hideTop? '&hideTop=1' : '';
        url += options.hideMenu? '&hideMenu=1' : '';
        url += options.frameless? '&frameless=true' : '';
      }
      return url;
    };

    switch (type) {
      case GA1Service.URLTYPE_ORDER_SUPPORT:
        return `/unacc/respond?Dereqid=${params.orderId}`;
      case GA1Service.URLTYPE_SURVEY_EDIT:
        return this.authService.authenticatedUrl('/unacc/AnswerView_Survey.jsp?ejbEvent=update&Dewflty=BP&Deccoid=' + params.companyCountryId);
      case GA1Service.URLTYPE_CREATE_COMPANY:
        return this.getMainUnaccUrl('CompanyNewView_Edit.jsp?ejbEvent=create');
      case GA1Service.URLTYPE_ACSL_TC:
        return `#/faqs?frameless=true&keyword=T%26C`;
      case GA1Service.URLTYPE_BILLING_TYPE:
        return '/unacc/BillingType.htm';
      case GA1Service.URLTYPE_WEBSHOP_VIEW_MARGINS:
        return getUrl('/unacc/WP_MarginsView_Browse.jsp', params, options);
      case GA1Service.URLTYPE_WEBSHOP_NEW_MARGIN:
        return getUrl('/unacc/WP_MarginsView_Edit.jsp', params, options);
      case GA1Service.URLTYPE_CREATE_LOCATION:
        return getUrl('/unacc/C_LocatView_Edit.jsp', params, options);
      case GA1Service.URLTYPE_UPLOAD_INVOICE_ADDRESSES:
        return getUrl('/unacc/ImportLocationView_Edit.jsp', params, options);
      case GA1Service.URLTYPE_SUPPORT_TICKET:
        return getUrl('/unacc/MaintDetView_Edit.jsp', params, options);
      case GA1Service.URLTYPE_UPLOAD_CATALOG:
        return getUrl('/unacc/ImportPriceView_Edit.jsp', params, options);
      default:
        return undefined;
    }
  }

  public getEmbeddedGA1Url(url:string, hideTop?:boolean, hideMenu?:boolean):string {
    let ga1Url:string = '/unacc/Index.jsp?URL=';
    ga1Url += encodeURIComponent(url);
    ga1Url += hideTop? '&hideTop=1' : '';
    ga1Url += hideMenu? '&hideMenu=1' : '';
    return this.authService.authenticatedUrl(ga1Url);
  }

  private getMainUnaccUrl(ga1Url:string):string {
    if (ga1Url.search(/\/unacc\//) !== 0) {
      ga1Url = '/unacc/' + ga1Url;
    }

    return `/${this.gaStateService.getGAVersion()}/#/unacc?url=${encodeURIComponent(ga1Url)}`;
  }

  private getTargetOrigin():string {
    if (!this.targetOrigin) {
      this.targetOrigin = this.window.location.href.substring(0, this.window.location.href.indexOf('/', 8)); //e.g. https://localhost:8443
    }
    return this.targetOrigin;
  }

  private productAdded(messageId:string, data:any):void {
    //resolve the promise and cancel the expiryTimer
    const addingProduct = this.addingProducts[ messageId ];
    if (addingProduct) {
      addingProduct.promise.resolve([
        messageId,
        data
      ]);
      clearTimeout(addingProduct.expireTimer);
      this.addingProducts[ messageId ] = undefined;
    } else {
      console.warn('Unknown product added: ' + messageId);
    }
  }

  private productAddExpired(messageId:number):void {
    //reject the promise
    const addingProduct = this.addingProducts[ messageId ];
    if (addingProduct) {
      addingProduct.promise.reject('Expired');
      this.addingProducts[ messageId ] = undefined;
    } else {
      console.warn('Unknown productAdd expired: ' + messageId);
    }
  }

  private onMessage = (e:JQueryEventObject):void => {
    try {
      const message = util.fromJson((<any>e.originalEvent).data);

      console.debug('GA2 message received', message.message);

      if (message.data && typeof message.data === 'string') {
        message.data = JSON.parse(message.data);
      }
      if (message.message === 'addedToCart') {
        this.productAdded(message.messageId, message.data);
      }
      if (message.message === 'updateCart') {
        this.broadcastEvent('updateCart', message.data);
      }
      // close the dialog with 'edit survey' when the message 'closeDialog' is received
      if (message.message === MESSAGE_CLOSE_DIALOG) {
        this.broadcastEvent(EVENT_CLOSE_DIALOG, message.data);
      }
      if (message.message === 'logout') {
        this.authService.performLogout();
      }
      if (message.message === MESSAGE_GOTO_HOMEPAGE) {
        this.broadcastEvent(EVENT_GOTO_HOMEPAGE);
      }
      if (message.message === 'sessionDestroyed') {
        this.broadcastEvent(ConnectionService.EVENT_SESSION_DESTROYED);
        this.gaEventService.sendMessage(ConnectionService.EVENT_SESSION_DESTROYED);
      }
      if (message.message === MESSAGE_TICKETGUIDE_SHOW) {
        this.broadcastEvent(EVENT_TICKETDIALOG_SHOW, message.data);
      }
      if (message.message === MESSAGE_MENU_CHANGED) {
        this.broadcastEvent(EVENT_MENU_CHANGED, message);
      }
      if (message.message === MESSAGE_MENU_READY) {
        this.broadcastEvent(EVENT_MENU_READY, message);
      }
      if (message.message === MESSAGE_EDITMODE_CHANGED) {
        this.editMode = message.enabled;
        this.broadcastEvent(EVENT_EDITMODE_CHANGED);
      }
      if (message.message === MESSAGE_SAVE_ORDER) {
        this.broadcastEvent(EVENT_SAVE_ORDER, message);
      }
      if (message.message === MESSAGE_CHECK_SOW) {
        this.broadcastEvent(EVENT_CHECK_SOW, message);
      }
      if (message.message === MESSAGE_CHANGE_BILLING_TYPE) {
        this.broadcastEvent(EVENT_CHANGE_BILLING_TYPE, message);
      }
      if (message.message === MESSAGE_OPEN_BROWSELIST_EDIT_DIALOG) {
        this.broadcastEvent(EVENT_OPEN_BROWSELIST_EDIT_DIALOG, message.data);
      }
      if (message.message === MESSAGE_OPEN_SEND_INVOICE_MAIL_DIALOG) {
        this.broadcastEvent(EVENT_OPEN_SEND_INVOICE_MAIL_DIALOG, message.data);
      }
    } catch (exc) {
      // empty block
    }

  };

  /**
   * @ngdoc method
   * @name ga2.shared.services:ga1Service#addToCart
   * @methodOf ga2.shared.services:ga1Service
   * @description
   *  Adds a product to the users cart (by using `window.postMessage` to send it to GA1)
   * @param {Array} items an array of items to be added to the cart.
   *  The objects therein should at least have an 'id' and a 'count' property
   * @returns {Promise} a promise that will be resolved when GA1 acknowledges, or rejected when there is a timeout (5 seconds).
   */
  public addToCart(items:IGA1OutgoingCartItem[]):Promise<any> {
    const messageId:number = this.nextMessageId++;

    const addingProduct = {
      promise: this.deferredService.defer(),
      expireTimer: setTimeout(() => {
        this.productAddExpired(messageId);
      }, this.EXPIRY)
    };

    this.addingProducts[ messageId ] = addingProduct;

    this.sendMessage('addToCart', items, messageId);

    return addingProduct.promise.promise;
  }

  public askCurrentCart():void {
    this.sendMessage(MESSAGE_GET_CART);
  }

  // edit mode of translation labels in ga2
  public get editMode():boolean {
    return this._editMode;
  }

  public set editMode(editMode:boolean) {
    this._editMode = editMode;
  }

  private broadcastEvent(name:string, data?:IGAEvent):void {
    this._subject.next({ name, data });
  }
}
